import {
  imageIdFormat,
  locationIdFormat,
  productFamilyIdFormat,
  productIdFormat,
} from "@redotech/redo-model/ids";
import { AttributeSchema } from "@redotech/redo-model/inventory/attributes";
import { ListingType } from "@redotech/redo-model/inventory/base-listing";
import { ListingsWithStatusSchema } from "@redotech/redo-model/inventory/listing-status";
import { ProductMappingSchema } from "@redotech/redo-model/inventory/listing-types";
import { WeightSchema } from "@redotech/redo-model/outbound-labels/util";
import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod/v4";

export const LoadedProductMappingSchema = z.object({
  shopifyMappings: ProductMappingSchema[ListingType.Shopify],
  redoMarketplaceMappings: ProductMappingSchema[ListingType.RedoMarketplace],
  chatGptMappings: ProductMappingSchema[ListingType.ChatGpt],
  ebayMappings: ProductMappingSchema[ListingType.Ebay],
});
export type LoadedProductMapping = z.infer<typeof LoadedProductMappingSchema>;

const LocationInventorySchema = z.object({
  id: locationIdFormat.schema,
  name: z.string(),
  quantity: z.bigint(),
  committed: z.bigint(),
  reserved: z.bigint(),
  available: z.bigint(),
});

export type LocationInventoryData = z.infer<typeof LocationInventorySchema>;

const ImageSchema = z.object({
  id: imageIdFormat.schema,
  url: z.string(),
  altText: z.string(),
  position: z.number(),
  generated: z.boolean().nullish(),
  feedback: z.enum(["positive", "negative"]).nullish(),
});

const RedoProductDataSchema = z.object({
  id: productIdFormat.schema,
  productFamilyId: productFamilyIdFormat.schema,
  title: z.string(),
  description: z.string(),
  vendor: z.string().nullish(),
  tags: z.array(z.string()),
  price: z.number().nullish(),
  cost: z.number().nullish(),
  currency: z.string().nullish(),
  sku: z.string().nullish(),
  upc: z.string().nullish(),
  barcodes: z.array(z.string()),
  weight: WeightSchema.nullish(),
  taxCode: z.string().nullish(),
  taxable: z.boolean().nullish(),
  physicalItem: z.boolean().nullish(),
  originCountry: z.string().nullish(),
  hsCode: z
    .object({
      code: z.string(),
      description: z.string(),
    })
    .nullish(),
  source: z.string().nullish(),
  source_id: z.string().nullish(),
  image: z.object({ altText: z.string(), url: z.string() }).nullish(),
  images: z.array(ImageSchema),
  attributes: z.array(AttributeSchema),
  mappings: LoadedProductMappingSchema,
  listings: ListingsWithStatusSchema,
  locations: z.array(LocationInventorySchema),
  stockout_date: z.instanceof(Temporal.PlainDate).nullish(),
  // Reorder parameters
  moq: z.number().nullish(),
  leadTime: z.number().nullish(),
  buffer: z.number().nullish(),
  coverageDays: z.number().nullish(),
});

export type RedoProductData = z.infer<typeof RedoProductDataSchema>;

export const getRedoProduct = unaryRpc({
  name: "getRedoProduct",
  input: z.object({ productId: productIdFormat.schema }),
  output: RedoProductDataSchema,
});
